import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  CircularProgress,
  Slider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import AddOrEditAddressDialog from './components/AddAddress';
import { formatAddressDisplay } from 'components/address/formatAddressDisplay';
import api from 'utils/api';
import OfferClaimPrice from '../../../OfferClaimPrice';

const formatPhone = (input = "") => {
  const digits = input.replace(/\D/g, "").slice(0, 10);
  if (!digits) return "";
  let formatted = "";
  if (digits.length < 4) formatted = `(${digits}`;
  else if (digits.length < 7) formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  else formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return formatted;
};

const mapCompanyInfoToAddress = (companyInfo) => {
  if (!companyInfo?.street) {
    return null;
  }

  return {
    addressId: 0,
    businessId: companyInfo.businessId,
    companyName: companyInfo.companyName || '',
    street: companyInfo.street || '',
    addressLine2: companyInfo.addressLine2 || '',
    zipCode: companyInfo.zipCode || '',
    city: companyInfo.city || '',
    state: companyInfo.state || '',
    siteUrl: companyInfo.siteUrl || '',
    logoUrl: companyInfo.logoUrl || '',
    latitude: companyInfo.latitude ?? null,
    longitude: companyInfo.longitude ?? null,
    countryCode: companyInfo.countryCode || 'US',
    countryName: companyInfo.countryName || '',
    phone: companyInfo.phoneNumber || companyInfo.phone || '',
  };
};

const mergeWithBusinessProfileAddresses = (apiAddresses, companyInfo) => {
  const list = Array.isArray(apiAddresses) ? apiAddresses : [];
  const primaryAddress = mapCompanyInfoToAddress(companyInfo);

  if (!primaryAddress) {
    return list;
  }

  const hasPrimaryAddress = list.some((address) => address.addressId === 0);
  if (hasPrimaryAddress) {
    return list;
  }

  return [primaryAddress, ...list];
};

const buildLocationEntry = (address, item, phoneTogglesState, addressTogglesState) => {
  const showPhone = phoneTogglesState.hasOwnProperty(address.addressId)
    ? phoneTogglesState[address.addressId]
    : Boolean(address.phone);

  const showAddress = addressTogglesState.hasOwnProperty(address.addressId)
    ? addressTogglesState[address.addressId]
    : true;

  return {
    address: formatAddressDisplay(address),
    lat: address.latitude,
    lng: address.longitude,
    radius: item.radius,
    addressId: address.addressId,
    showPhone,
    phoneNumber: address.phone || null,
    showAddress,
  };
};

const LocationLocal = ({ error, updateLocation, value=[] }) => {
  const [addresses, setAddresses] = useState([]);
  const [selectedAddresses, setSelectedAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const companyInfo = useSelector((state) => state.business.companyInfo);
  const businessId = useSelector(
    (state) => state.business.businessId || state.business.companyInfo?.businessId
  );

  const [openAddressDialog, setOpenAddressDialog] = useState(false);
  const [refreshAddresses, setRefreshAddresses] = useState(false);
  const [phoneToggles, setPhoneToggles] = useState({}); // Track which addresses have phone enabled
  const [addressToggles, setAddressToggles] = useState({}); // Track which addresses have address enabled (default ON)


  useEffect(() => {
    setIsHydrated(false);
    setSelectedAddresses([]);
    setAddresses([]);
  }, [businessId]);

  useEffect(() => {
    if (!businessId) {
      setAddresses(mergeWithBusinessProfileAddresses([], companyInfo));
      setLoading(false);
      return;
    }

    setLoading(true);

    api
      .get(`/business/${businessId}/addresses`)
      .then((data) => {
        setAddresses(mergeWithBusinessProfileAddresses(data, companyInfo));
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching addresses:', error);
        setAddresses(mergeWithBusinessProfileAddresses([], companyInfo));
        setLoading(false);
      });
  }, [businessId, refreshAddresses, companyInfo]);

  // Hydrate selection AFTER both value and addresses are available
const [isHydrated, setIsHydrated] = useState(false);

useEffect(() => {
  if (!isHydrated && addresses.length > 0) {
    // If value exists (editing existing offer), hydrate from DB values
    if (value.length > 0) {
      const matched = value.map((item) => {
        const addressId = item.addressId ?? item.id?.addressId;
        const radius = item.radius;

        const exists = addresses.some(addr => addr.addressId === addressId);
        if (!exists || addressId == null) return null;

        return { addressId, radius };
      }).filter(Boolean);

      setSelectedAddresses(matched);

      // Hydrate phone toggles from existing DB values
      const toggles = {};
      const addressTogglesMap = {};
      value.forEach((item) => {
        const addressId = item.addressId ?? item.id?.addressId;
        if (addressId != null) {
          // Set toggle based on showPhone from DB (true if showPhone is true, false otherwise)
          toggles[addressId] = item.showPhone === true;
          // Set address toggle based on showAddress from DB (default to true if not present)
          addressTogglesMap[addressId] = item.showAddress !== undefined ? item.showAddress === true : true;
        }
      });
      setPhoneToggles(toggles);
      setAddressToggles(addressTogglesMap);
    } else {
      const allSelectedAddresses = addresses.map((address) => ({
        addressId: address.addressId,
        radius: 15,
      }));
      const newPhoneToggles = {};
      const newAddressToggles = {};

      addresses.forEach((address) => {
        if (address.phone) {
          newPhoneToggles[address.addressId] = true;
        }
        newAddressToggles[address.addressId] = true;
      });

      setSelectedAddresses(allSelectedAddresses);
      setPhoneToggles(newPhoneToggles);
      setAddressToggles(newAddressToggles);

      const locationArray = allSelectedAddresses
        .map((item) => {
          const address = addresses.find((addr) => addr.addressId === item.addressId);
          if (!address) {
            return null;
          }

          return buildLocationEntry(address, item, newPhoneToggles, newAddressToggles);
        })
        .filter(Boolean);

      updateLocation(locationArray);
    }

    setIsHydrated(true); // Prevent future hydration
  }
}, [addresses, value, isHydrated, updateLocation]);


//   useEffect(() => {
//   if (!offerId || addresses.length === 0) return;

//   axios
//     .get(`/api/business/${businessId}/${offerId}/SelectedAddresses`)
//     .then((response) => {
//       const selectedData = response.data.map((item) => ({
//         addressId: item.addressId,
//         radius: item.radius || 15,
//       }));
//       setSelectedAddresses(selectedData);
//     })
//     .catch((error) =>
//       console.error('Error fetching selected addresses:', error)
//     );
// }, [offerId, businessId, addresses]);


  const handleCheckboxChange = (event, addressId) => {
    const isChecked = event.target.checked;

    let updatedSelectedAddresses = [...selectedAddresses];
    let updatedPhoneToggles = { ...phoneToggles };
    let updatedAddressToggles = { ...addressToggles };
    
    if (isChecked) {
      const matchedAddress = selectedAddresses.find(
        (item) => item.addressId === addressId
      );
      updatedSelectedAddresses.push({
        addressId,
        radius: matchedAddress ? matchedAddress.radius : 15,
      });
      
      // When selecting an address for the first time, set phone toggle to ON by default
      // Only if it doesn't already exist in phoneToggles (new selection)
      if (!phoneToggles.hasOwnProperty(addressId)) {
        const address = addresses.find((addr) => addr.addressId === addressId);
        // Only set to true if address has a phone number
        if (address && address.phone) {
          updatedPhoneToggles[addressId] = true; // Default to ON for new selections
        }
      }
      // When selecting an address for the first time, set address toggle to ON by default
      if (!addressToggles.hasOwnProperty(addressId)) {
        updatedAddressToggles[addressId] = true; // Default to ON for new selections
      }
    } else {
      updatedSelectedAddresses = updatedSelectedAddresses.filter(
        (item) => item.addressId !== addressId
      );
    }

    // Update state
    setSelectedAddresses(updatedSelectedAddresses);
    setPhoneToggles(updatedPhoneToggles);
    setAddressToggles(updatedAddressToggles);
    
    // Update location data with the updated toggles
    const locationArray = updatedSelectedAddresses.map((item) => {
      const address = addresses.find((addr) => addr.addressId === item.addressId);
      if (!address) {
        console.warn('Missing address for addressId:', item.addressId);
        return null;
      }
      const fullAddress = formatAddressDisplay(address);
      return {
        address: fullAddress,
        lat: address.latitude,
        lng: address.longitude,
        radius: item.radius,
        addressId: address.addressId,
        showPhone: updatedPhoneToggles[address.addressId] || (address.phone ? true : false),
        phoneNumber: address.phone || null,
        showAddress: updatedAddressToggles[address.addressId] !== undefined ? updatedAddressToggles[address.addressId] : true,
      };
    }).filter(Boolean);
    
    console.log("📦 updateLocation sending to parent:", locationArray);
    updateLocation(locationArray);
  };

  const handleRadiusChange = (event, newValue, addressId) => {
    const updatedSelectedAddresses = selectedAddresses.map((item) =>
      item.addressId === addressId ? { ...item, radius: newValue } : item
    );

    setSelectedAddresses(updatedSelectedAddresses);
    updateLocationData(updatedSelectedAddresses);
  };

  const handleTextChange = (event, addressId) => {
    const newRadius = event.target.value;

    const updatedSelectedAddresses = selectedAddresses.map((item) =>
      item.addressId === addressId ? { ...item, radius: newRadius } : item
    );

    setSelectedAddresses(updatedSelectedAddresses);
  };

  const handleTextBlur = (event, addressId) => {
    let newRadius = parseInt(event.target.value, 10) || 0;

    if (newRadius < 2) newRadius = 2;
    if (newRadius > 100) newRadius = 100;

    const updatedSelectedAddresses = selectedAddresses.map((item) =>
      item.addressId === addressId ? { ...item, radius: newRadius } : item
    );

    setSelectedAddresses(updatedSelectedAddresses);
    updateLocationData(updatedSelectedAddresses);
  };

  const handleSelectAll = (event) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      const allSelectedAddresses = addresses.map((address) => ({
        addressId: address.addressId,
        radius: 15,
      }));
      
      // Set phone toggles to ON by default for all addresses that have phone numbers
      const newPhoneToggles = { ...phoneToggles };
      const newAddressToggles = { ...addressToggles };
      addresses.forEach((address) => {
        // Only set to ON if address has phone and toggle doesn't already exist
        if (address.phone && !newPhoneToggles.hasOwnProperty(address.addressId)) {
          newPhoneToggles[address.addressId] = true; // Default to ON
        }
        // Set address toggle to ON by default if it doesn't already exist
        if (!newAddressToggles.hasOwnProperty(address.addressId)) {
          newAddressToggles[address.addressId] = true; // Default to ON
        }
      });
      
      setSelectedAddresses(allSelectedAddresses);
      setPhoneToggles(newPhoneToggles);
      setAddressToggles(newAddressToggles);
      
      // Update location data with the updated toggles
      const locationArray = allSelectedAddresses.map((item) => {
        const address = addresses.find((addr) => addr.addressId === item.addressId);
        if (!address) {
          console.warn('Missing address for addressId:', item.addressId);
          return null;
        }
        const fullAddress = formatAddressDisplay(address);
        return {
          address: fullAddress,
          lat: address.latitude,
          lng: address.longitude,
          radius: item.radius,
          addressId: address.addressId,
          showPhone: newPhoneToggles[address.addressId] || (address.phone ? true : false),
          phoneNumber: address.phone || null,
          showAddress: newAddressToggles[address.addressId] !== undefined ? newAddressToggles[address.addressId] : true,
        };
      }).filter(Boolean);
      
      console.log("📦 updateLocation sending to parent:", locationArray);
      updateLocation(locationArray);
    } else {
      setSelectedAddresses([]);
      setPhoneToggles({});
      setAddressToggles({});
      updateLocation([]);
    }
  };

  const areAllSelected =
    addresses.length > 0 &&
    selectedAddresses.length === addresses.length;

const updateLocationData = (selectedAddresses) => {
  const locationArray = selectedAddresses.map((item) => {
    const address = addresses.find((addr) => addr.addressId === item.addressId);

    if (!address) {
      console.warn('Missing address for addressId:', item.addressId);
      return null; // skip or handle appropriately
    }

    // Default to true (ON) for new selections if address has phone and toggle doesn't exist
    // For existing selections, use the toggle value or default to false
    const showPhone = phoneToggles.hasOwnProperty(address.addressId) 
      ? phoneToggles[address.addressId] 
      : (address.phone ? true : false); // Default to ON if address has phone, OFF if no phone

    // Default to true (ON) for address toggle
    const showAddress = addressToggles.hasOwnProperty(address.addressId)
      ? addressToggles[address.addressId]
      : true; // Default to ON

        const fullAddress = formatAddressDisplay(address);
    return {
      address: fullAddress,
      lat: address.latitude,
      lng: address.longitude,
      radius: item.radius,
      addressId: address.addressId,
      showPhone: showPhone,
      phoneNumber: address.phone || null,
      showAddress: showAddress,
    };
  }).filter(Boolean); // remove nulls

  console.log("📦 updateLocation sending to parent:", locationArray);
  updateLocation(locationArray);
};

  const handleAddAddress = () => {
    setOpenAddressDialog(true);
  };

  const handleCloseAddressDialog = () => {
    setOpenAddressDialog(false);
  };

  const handleAddressesUpdated = (newAddress) => {
    setRefreshAddresses(prev => !prev); // or push to state if needed
  };

  const handlePhoneToggle = (addressId, enabled) => {
    console.log("🔄 Phone toggle changed:", addressId, "to", enabled);
    console.log("📱 Current toggles before update:", phoneToggles);
    
    // Calculate the updated toggles immediately
    const updatedToggles = {
      ...phoneToggles,
      [addressId]: enabled
    };
    
    console.log("📱 Updated toggles after calculation:", updatedToggles);
    
    // Update the toggle state
    setPhoneToggles(updatedToggles);
    
    // Update location data immediately with the calculated toggle values
    const locationArray = selectedAddresses.map((item) => {
      const address = addresses.find((addr) => addr.addressId === item.addressId);

      if (!address) {
        console.warn('Missing address for addressId:', item.addressId);
        return null;
      }

      // Use the updated toggle value for this address, or default based on phone existence
      const showPhone = updatedToggles[address.addressId] !== undefined
        ? updatedToggles[address.addressId]
        : (address.phone ? true : false);

      // Get address toggle value
      const showAddress = addressToggles.hasOwnProperty(address.addressId)
        ? addressToggles[address.addressId]
        : true;

      console.log(`📍 Address ${address.addressId}: showPhone=${showPhone} (toggle=${updatedToggles[address.addressId]}, hasPhone=${!!address.phone})`);

      const fullAddress = formatAddressDisplay(address);
      return {
        address: fullAddress,
        lat: address.latitude,
        lng: address.longitude,
        radius: item.radius,
        addressId: address.addressId,
        showPhone: showPhone,
        phoneNumber: address.phone || null,
        showAddress: showAddress,
      };
    }).filter(Boolean);

    console.log("📦 updateLocation sending to parent (toggle changed):", locationArray);
    updateLocation(locationArray);
  };

  const handleAddressToggle = (addressId, enabled) => {
    // Calculate the updated toggles immediately
    const updatedToggles = {
      ...addressToggles,
      [addressId]: enabled
    };
    
    // Update the toggle state
    setAddressToggles(updatedToggles);
    
    // Update location data immediately with the calculated toggle values
    const locationArray = selectedAddresses.map((item) => {
      const address = addresses.find((addr) => addr.addressId === item.addressId);

      if (!address) {
        console.warn('Missing address for addressId:', item.addressId);
        return null;
      }

      // Get phone toggle value
      const showPhone = phoneToggles.hasOwnProperty(address.addressId)
        ? phoneToggles[address.addressId]
        : (address.phone ? true : false);

      // Use the updated address toggle value
      const showAddress = updatedToggles[address.addressId] !== undefined
        ? updatedToggles[address.addressId]
        : true;

      const fullAddress = formatAddressDisplay(address);
      return {
        address: fullAddress,
        lat: address.latitude,
        lng: address.longitude,
        radius: item.radius,
        addressId: address.addressId,
        showPhone: showPhone,
        phoneNumber: address.phone || null,
        showAddress: showAddress,
      };
    }).filter(Boolean);

    updateLocation(locationArray);
  };

  if (loading) {
    return (
      <Box
        mt={4}
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" mt={2}>
          Loading, please wait...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <OfferClaimPrice regionType="LOCAL" localTargets={selectedAddresses} />

      <Box  sx={{ width: '100%' }}>
        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <Typography variant="body2" color="text.secondary">
            Select business addresses and set radius:
          </Typography>
          <Button onClick={handleAddAddress}>
            Add address
          </Button>
        </Box>
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 300,
            overflow: 'scroll',
            mt: 2,
            border: '1px solid #ccc',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
            '& .MuiTableCell-root': { py: 0.5, px: 1 },
          }}
        >
          <style>
            {`
              ::-webkit-scrollbar {
                width: 12px;
              }
              ::-webkit-scrollbar-thumb {
                background-color: #888;
                border-radius: 10px;
              }
              ::-webkit-scrollbar-thumb:hover {
                background-color: #555;
              }
              ::-webkit-scrollbar-track {
                background-color: #f0f0f0;
              }
            `}
          </style>
          <Box sx={{ width: '100%', height: '100%', overflow: 'scroll' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="caption" color="text.primary" sx={{ lineHeight: 1, mb: 0.5 }}>
                        Select all
                      </Typography>
                      <Checkbox
                        checked={areAllSelected}
                        onChange={handleSelectAll}
                        size="small"
                        sx={{ p: 0.5 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.primary">Street</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.primary">City</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.primary">State</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.primary">Zip code</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="body2" color="text.primary">Address</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        (hide/show)
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" flexDirection="column" alignItems="center">
                      <Typography variant="body2" color="text.primary">Phone</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        (hide/show)
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="text.primary">Radius (miles)</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {addresses.map((address) => {
                  const selectedAddress = selectedAddresses.find(
                    (item) => item.addressId === address.addressId
                  );
                  const isSelected = Boolean(selectedAddress);
                  const selectedRadius = selectedAddress?.radius;
                  const addressLabel = formatAddressDisplay(address);

                  return (
                    <TableRow key={address.addressId}>
                      <TableCell align="center">
                        <Checkbox
                          checked={isSelected}
                          inputProps={{ 'aria-label': `Select ${addressLabel}` }}
                          onChange={(event) =>
                            handleCheckboxChange(event, address.addressId)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        {[address.street, address.addressLine2].filter(Boolean).join(', ') || '—'}
                      </TableCell>
                      <TableCell align="center">{address.city}</TableCell>
                      <TableCell align="center">{address.state}</TableCell>
                      <TableCell align="center">{address.zipCode}</TableCell>
                      <TableCell align="center">
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={isSelected && (
                                // If toggle exists in state, use its value (can be true or false)
                                // If not in state yet, default to true (ON)
                                address.addressId in addressToggles
                                  ? Boolean(addressToggles[address.addressId])
                                  : true // Default to ON
                              )}
                              disabled={!isSelected}
                              inputProps={{ 'aria-label': `Show address for ${addressLabel}` }}
                              onChange={(e) => {
                                e.stopPropagation();
                                const newValue = e.target.checked;
                                handleAddressToggle(address.addressId, newValue);
                              }}
                              color="primary"
                              onClick={(e) => e.stopPropagation()} // Prevent row selection when clicking toggle
                            />
                          }
                          label=""
                          sx={{ m: 0 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                          <Typography variant="body2">
                            {address.phone && address.phone.trim() !== '' 
                              ? formatPhone(address.phone) 
                              : 'N/A'}
                          </Typography>
                          {address.phone && address.phone.trim() !== '' && (
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={isSelected && (
                                    // If toggle exists in state, use its value (can be true or false)
                                    // If not in state yet, default to true if address has phone
                                    address.addressId in phoneToggles
                                      ? Boolean(phoneToggles[address.addressId])
                                      : Boolean(address.phone) // Default to ON if address has phone
                                  )}
                                  disabled={!isSelected}
                                  inputProps={{ 'aria-label': `Show phone for ${addressLabel}` }}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    const newValue = e.target.checked;
                                    console.log("🔘 Phone Switch onChange:", address.addressId, "checked:", newValue);
                                    handlePhoneToggle(address.addressId, newValue);
                                  }}
                                  color="primary"
                                  onClick={(e) => e.stopPropagation()} // Prevent row selection when clicking toggle
                                />
                              }
                              label=""
                              sx={{ m: 0 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {addresses.length <= 0 ? (
                          <Box>
                            <Slider
                              value={selectedRadius || 15}
                              onChange={(event, newValue) =>
                                handleRadiusChange(event, newValue, address.addressId)
                              }
                              min={2}
                              max={100}
                              step={1}
                              sx={{ width: 150 }}
                            />
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              align="center"
                            >
                              Radius: {selectedRadius || 15} miles
                            </Typography>
                          </Box>
                        ) : (
                          <TextField
                            value={selectedRadius || ''}
                            disabled={!isSelected}
                            inputProps={{
                              'aria-label': `Radius for ${addressLabel}`,
                              min: 2,
                              max: 100,
                              step: 1,
                              style: { textAlign: 'center' },
                            }}
                            onChange={(event) =>
                              handleTextChange(event, address.addressId)
                            }
                            onBlur={(event) =>
                              handleTextBlur(event, address.addressId)
                            }
                            type="number"
                            sx={{ width: 80 }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </TableContainer>
        {error && selectedAddresses.length === 0 && (
          <Typography variant="body2" color="error" mt={2}>
            Please select at least one business address.
          </Typography>
        )}
      </Box>
      <Dialog
        open={openAddressDialog}
        onClose={handleCloseAddressDialog}
        fullWidth
        maxWidth="md"
      >
        <Box position="relative">
    {/* Close Button */}
    <IconButton
      aria-label="close"
      onClick={handleCloseAddressDialog}
      sx={{
        position: 'absolute',
        right: 8,
        top: 8,
        color: (theme) => theme.palette.grey[500],
        zIndex: 1,
      }}
    >
      <CloseIcon />
    </IconButton>

    {/* Dialog Content */}
    <AddOrEditAddressDialog
    open={openAddressDialog}
    onClose={handleCloseAddressDialog}
    onAddressAdded={handleAddressesUpdated}
  />
        </Box>
      </Dialog>
    </>
  );
};

export default LocationLocal;
